import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { socialMediaLinkService } from "@/services/socialMediaLinkService";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { useFooterSettings } from "@/hooks/useFooterSettings";
import { cn } from "@/lib/utils";

/**
 * 页脚组件
 * 包含链接、版权信息和备案号
 * 已优化：统一数据流、移除硬编码缓存、增加揭晓动画
 */
export default function Footer() {
  const { t } = useTranslation();
  const { isDomestic, loading: isRegionLoading } = useOperationRegion();

  // 1. 数据获取
  const { data: socialLinks = [], isLoading: isSocialLoading } = useQuery({
    queryKey: ["socialMediaLinks"],
    queryFn: () => socialMediaLinkService.getLinks(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: settings, isLoading: isSettingsLoading } = useFooterSettings();

  // 2. 状态判断
  const isLoaded = !isRegionLoading && !isSettingsLoading && !isSocialLoading;
  const hasVisibleLinks =
    settings?.about_visible ||
    settings?.terms_visible ||
    settings?.privacy_visible;

  return (
    <footer
      className={cn(
        "bg-background border-t border-border pt-8 pb-8 w-full transition-opacity duration-500",
        isLoaded ? "opacity-100" : "opacity-0 pointer-events-none"
      )}
    >
      <div className="mx-auto flex flex-col items-center w-full max-w-7xl px-4">
        <div className="w-full flex flex-col gap-6 text-xs sm:flex-row sm:items-center sm:justify-between">
          {/* 左侧：链接与版权 */}
          <div className="flex flex-col gap-3 text-left sm:flex-1">
            {hasVisibleLinks && (
              <nav
                aria-label="Footer Navigation"
                className="flex flex-row flex-wrap items-center gap-5 text-muted-foreground font-medium"
              >
                {settings?.about_visible && (
                  <Link
                    to="/about"
                    className="hover:text-foreground transition-colors"
                  >
                    {t("footer.about")}
                  </Link>
                )}
                {settings?.terms_visible && (
                  <Link
                    to="/terms"
                    className="hover:text-foreground transition-colors"
                  >
                    {t("footer.terms")}
                  </Link>
                )}
                {settings?.privacy_visible && (
                  <Link
                    to="/privacy"
                    className="hover:text-foreground transition-colors"
                  >
                    {t("footer.privacy")}
                  </Link>
                )}
              </nav>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2 text-muted-foreground/60">
              {isDomestic ? (
                <>
                  <span>
                    吞舟 Tunzo® | Copyright © 2026 分贝视界 | All Rights
                    Reserved
                  </span>
                  <span className="hidden sm:inline opacity-30">|</span>
                  <a
                    href="https://beian.miit.gov.cn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground transition-colors"
                  >
                    粤ICP备2021124805号
                  </a>
                </>
              ) : (
                <span>Copyright © 2026 Tunzo. All Rights Reserved.</span>
              )}
            </div>
          </div>

          {/* 右侧：社交媒体、主题、语言 */}
          <div className="flex flex-wrap justify-start sm:justify-end items-center gap-3 sm:flex-none">
            {socialLinks.length > 0 && (
              <div
                className="flex items-center gap-4 mr-2"
                aria-label="Social Media Links"
              >
                {socialLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground/80 hover:text-foreground transition-transform hover:scale-110 duration-200"
                    title={link.name}
                    aria-label={link.name}
                  >
                    <div
                      className="w-5 h-5 fill-current"
                      dangerouslySetInnerHTML={{ __html: link.svg_code }}
                    />
                  </a>
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5 ml-2 pl-4 border-l border-border/50">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
